import React, { FunctionComponent, useEffect } from 'react';
import { graphql } from 'react-relay';
import { usePaginationFragment } from 'react-relay/hooks';
import { CatalogListContent } from 'src/components/widgets/catalogList';
import { CatalogListItemModel } from 'src/components/widgets/catalogList/catalogListItemModel';
import { T } from 'src/shared/helpers';
import { RelayFragment } from 'src/shared/types';
import { useProductConfigurationState } from '../state';

import { productTemplate_listItem$data } from '../state/queries/__generated__/productTemplate_listItem.graphql';
import { templateCatalogListQuery$data } from './__generated__/templateCatalogListQuery.graphql';
import { templateCatalogPaginationFragmentQuery } from './__generated__/templateCatalogPaginationFragmentQuery.graphql';
import { templateCatalogPaginationFragment__productTemplates$key } from './__generated__/templateCatalogPaginationFragment__productTemplates.graphql';

// List component
interface ImportCatalogPaginationFragmentProps {
    query: templateCatalogListQuery$data;
    search: string;
    categoryID: number | null;
    onProductClick: (productTemplate: Omit<RelayFragment<productTemplate_listItem$data>, ' $fragmentType'>) => void;
}

const TemplateCatalogPaginationFragment: FunctionComponent<ImportCatalogPaginationFragmentProps> = (props) => {
    const { state } = useProductConfigurationState();
    const { data, loadNext, hasNext, refetch, isLoadingNext } = usePaginationFragment<
        templateCatalogPaginationFragmentQuery,
        templateCatalogPaginationFragment__productTemplates$key
    >(
        graphql`
            fragment templateCatalogPaginationFragment__productTemplates on Query
            @refetchable(queryName: "templateCatalogPaginationFragmentQuery")
            @argumentDefinitions(
                count: { type: "Int", defaultValue: 20 }
                cursor: { type: "String", defaultValue: "1" }
                search: { type: "String", defaultValue: "" }
                categoryID: { type: "Long", defaultValue: null }
            ) {
                productTemplates(first: $count, after: $cursor, search: $search, categoryID: $categoryID)
                    @connection(key: "templateCatalogPaginationFragment_productTemplates") {
                    edges {
                        node {
                            ... on Model {
                                id
                                name
                                largeImageUrl
                            }
                        }
                    }
                }
            }
        `,
        props.query
    );

    useEffect(() => {
        refetch(
            {
                count: 20,
                search: props.search,
                categoryID: props.categoryID
            },
            { fetchPolicy: 'store-and-network' }
        );
    }, [props.search]);

    const handleProductClick = (model: CatalogListItemModel) => {
        const productTemplate = data.productTemplates?.edges?.find((x) => x?.node?.id == model.id);
        if (productTemplate && productTemplate.node) {
            props.onProductClick(productTemplate.node);
        }
    };

    return (
        <CatalogListContent
            columns={{ xs: 3, md: 3, lg: 3 }}
            models={
                data.productTemplates?.edges?.map((x) => ({
                    id: x?.node?.id!,
                    subtitle: '',
                    title: x?.node?.name ?? '',
                    thumbnail: x?.node?.largeImageUrl,
                    selected: false
                })) ?? []
            }
            loadNext={() => loadNext(1)}
            hasNext={hasNext}
            isLoadingNext={isLoadingNext}
            onItemClick={handleProductClick}
            noItemsMessage={T._('No products found', 'Admin')}
        />
    );
};

export default TemplateCatalogPaginationFragment;
